import { completeQuestionnaire, getLocation, getOutcomePublic, getProductByIds, getQuestionnaireDetailsById, getQuestionnaireDetailsPreview, getThemeByQuestionnaireId } from "#services/questionnaire";
import { EStorageKeys, storageGet, storageSet } from "#services/storage";
import { generateId } from "#utils/generate-id";
import { useMutation, useQuery, useQueryClient } from 'react-query';
const WHITE_LABEL = 'WHITE_LABEL';
export const useQuestionnaireDetails = (id, preview, options) => useQuery([id], () => {
    if (preview) {
        return getQuestionnaireDetailsPreview(id);
    }
    return getQuestionnaireDetailsById(id);
}, {
    ...options
});
export const useQuestionnaireState = ({ questionnaireId, enabled }) => {
    const clientQuery = useQueryClient();
    return [
        useQuery([questionnaireId + '-state'], () => storageGet(questionnaireId + '-state').then((res) => {
            if (res) {
                if (!res.id || !res.questionnaireId) {
                    if (!res.id) {
                        res.id = generateId();
                    }
                    if (!res.questionnaireId) {
                        res.questionnaireId = questionnaireId;
                    }
                    storageSet(questionnaireId + '-state', res);
                }
                return res;
            }
            else {
                return storageSet(questionnaireId + '-state', {
                    id: generateId(),
                    questionnaireId,
                    answerSelectionIds: [],
                    questionFieldSubmissions: {},
                    fieldAnswer: {}
                });
            }
        }), { enabled, cacheTime: 0 }),
        useMutation((state) => storageSet(questionnaireId + '-state', state), {
            onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-state'])
        })
    ];
};
export const useCurrentQuestionId = ({ questionnaireId, enabled }) => useQuery([questionnaireId + '-current'], () => storageGet(questionnaireId + '-current'), { enabled, cacheTime: 0 });
export const useSetCurrentQuestionId = (questionnaireId) => {
    const clientQuery = useQueryClient();
    return useMutation((id) => storageSet(questionnaireId + '-current', id), {
        onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-current'])
    });
};
export const useQuestionQuestionnaire = ({ questionnaireId, enabled }) => useQuery([questionnaireId + '-questions'], () => storageGet(questionnaireId + '-questions'), {
    enabled,
    cacheTime: 0
});
export const useSetQuestionQuestionnaire = (questionnaireId) => {
    const clientQuery = useQueryClient();
    return useMutation((questions) => storageSet(questionnaireId + '-questions', questions), {
        onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-questions'])
    });
};
export const useQuestionnaireDate = ({ questionnaireId, enabled }) => useQuery([questionnaireId + '-date'], () => storageGet(questionnaireId + '-date'), { enabled, cacheTime: 0 });
export const useSetQuestionnaireDate = (questionnaireId) => {
    const clientQuery = useQueryClient();
    return useMutation((date) => storageSet(questionnaireId + '-date', date), {
        onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-date'])
    });
};
export const useQuestionnaireOutcomeId = ({ questionnaireId, enabled }) => useQuery([questionnaireId + '-outcomeId'], () => storageGet(questionnaireId + '-outcomeId'), {
    staleTime: 0,
    cacheTime: 0,
    enabled
});
export const useSetQuestionnaireOutcomeId = (questionnaireId) => {
    const clientQuery = useQueryClient();
    return useMutation((outcomeId) => storageSet(questionnaireId + '-outcomeId', outcomeId), {
        onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-outcomeId'])
    });
};
export const useQuestionnaireActiveStep = ({ questionnaireId, enabled }) => {
    const clientQuery = useQueryClient();
    return [
        useQuery([questionnaireId + '-activeStep'], () => storageGet(questionnaireId + '-activeStep').then((res) => {
            if (res) {
                return res;
            }
            else {
                return storageSet(questionnaireId + '-activeStep', 1);
            }
        }), { enabled, cacheTime: 0 }),
        useMutation((activeStep) => storageSet(questionnaireId + '-activeStep', activeStep), {
            onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-activeStep'])
        })
    ];
};
export const useQuestionnaireTheme = (questionnaireId, options) => useQuery([`theme-${questionnaireId}`], () => getThemeByQuestionnaireId(questionnaireId), {
    ...options
});
export const useCurrentTheme = (enabled) => useQuery([EStorageKeys.CURRENT_THEME], () => storageGet(EStorageKeys.CURRENT_THEME), { enabled });
export const useCompleteQuestionnaire = () => useMutation((params) => completeQuestionnaire(params));
export const useOutcomePublic = (id, getDataOutcome) => useQuery([id], () => getOutcomePublic(id), {
    retry: false,
    onSuccess: (data) => {
        if (getDataOutcome) {
            getDataOutcome(data);
        }
    },
    enabled: !!id
});
export const useProducts = (ids, options) => useQuery([ids.join(',')], () => getProductByIds(ids), {
    ...options
});
export const useCurrencyStorage = () => useQuery(['GET_CURRENCY_STORAGE'], () => {
    return storageGet(EStorageKeys.CURRENCY).then((data) => {
        return { name: data['Currency.Name'], symbol: data['Currency.Symbol'] };
    });
});
export const useGetLocationById = (id) => useQuery([`questionLocation-${id}`], () => getLocation(id), { enabled: Boolean(id) });
